import type { TourFormStepId } from '@/features/tour/tour-form-model'
import type { TourFormController } from './use-tour-form'
import { useTranslation } from 'react-i18next'
import { FormSectionCard } from '@/shared/ui/form-section-card'
import { PublishStatusSwitch } from '@/shared/ui/publish-status-switch'
import { GeneralInfoCard } from './general-info-card'
import { ImagesCard } from './images-card'
import { PoisCard } from './pois-card'
import { ScopeCard } from './scope-card'
import { TranslationsCard } from './translations-card'

interface StepContentProps {
  form: TourFormController
  step: TourFormStepId
}

/** Renders the cards of the open step; every card reads and writes the shared form state, so switching steps loses nothing. */
export function StepContent({ form, step }: StepContentProps) {
  const { t } = useTranslation()
  const { isEdit, isFormReady, languages, poiLanguage, state, options, pois, media, actions } = form
  const { basicInfo, translations, selectedPois, routeCoordinates, thumbnail, banner } =
    state.formState

  if (step === 'content') {
    return (
      <div className="space-y-6">
        <TranslationsCard
          isEdit={isEdit}
          isFormReady={isFormReady}
          languages={languages}
          translations={translations}
          onChange={state.setTranslations}
          entityId={state.translationEntityId}
          runningJobCount={state.pendingSuccessJobs.length}
          isSaving={actions.savingSection === 'translations'}
          isUpdating={actions.isUpdating}
          onSave={() => void actions.handleSaveTranslations()}
        />
        <GeneralInfoCard
          isEdit={isEdit}
          isFormReady={isFormReady}
          basicInfo={basicInfo}
          onChange={options.handleBasicInfoChange}
          isDirty={state.isBasicInfoDirty}
          isSaving={actions.savingSection === 'basicInfo'}
          isUpdating={actions.isUpdating}
          onSave={() => void actions.handleSaveBasicInfo()}
        />
      </div>
    )
  }

  if (step === 'scope') {
    return (
      <ScopeCard
        isEdit={isEdit}
        isFormReady={isFormReady}
        basicInfo={basicInfo}
        onChange={options.handleBasicInfoChange}
        countryOptions={options.countryOptions}
        provinceOptions={options.provinceOptions}
        tenantDestinationOptions={options.tenantDestinationOptions}
        isProvinceLoading={options.isProvincesLoading}
        isTenantDestinationLoading={options.isTenantDestinationsLoading}
        onCreateDestination={actions.handleCreateDestinationNavigation}
        isDirty={state.isBasicInfoDirty}
        isSaving={actions.savingSection === 'basicInfo'}
        isUpdating={actions.isUpdating}
        onSave={() => void actions.handleSaveBasicInfo()}
      />
    )
  }

  if (step === 'route') {
    return (
      <PoisCard
        isEdit={isEdit}
        isFormReady={isFormReady}
        selectedPois={selectedPois}
        routeCoordinates={routeCoordinates}
        onSelectedPoisChange={state.setSelectedPois}
        onRouteCoordinatesChange={state.setRouteCoordinates}
        selectedTenantDestination={options.selectedTenantDestination}
        poiPicker={pois}
        languageOptions={poiLanguage.languageOptions}
        languageCode={poiLanguage.effectiveLanguageCode}
        onLanguageChange={poiLanguage.setSelectedLanguageCode}
        isPoisBatchDirty={state.isPoisBatchDirty}
        onSavePois={() => void pois.handleSavePoisBatch()}
        isCoordinatesDirty={state.isCoordinatesDirty}
        isSavingCoordinates={actions.savingSection === 'coordinates'}
        isUpdating={actions.isUpdating}
        onSaveCoordinates={() => void actions.handleSaveCoordinates()}
      />
    )
  }

  return (
    <div className="space-y-6">
      <ImagesCard
        isEdit={isEdit}
        isFormReady={isFormReady}
        banner={banner}
        thumbnail={thumbnail}
        onBannerChange={state.setBanner}
        onThumbnailChange={state.setThumbnail}
        onBannerUploadingChange={media.setIsBannerUploading}
        onThumbnailUploadingChange={media.setIsThumbnailUploading}
        onPreview={media.setMediaPreviewUrl}
        isDirty={state.isMediaDirty}
        isSaving={actions.savingSection === 'media'}
        isSaveBlocked={actions.isUpdating || media.isMediaUploading}
        onSave={() => void actions.handleSaveMedia()}
      />
      {/* * VI: Trạng thái xuất bản chỉ chọn lúc tạo; tour đã có được bật/tắt từ danh sách. | EN: Publish state is chosen on create only; an existing tour is (de)activated from the list. */}
      {!isEdit && (
        <FormSectionCard title={t('tour.publish', { defaultValue: 'Xuất bản' })}>
          <PublishStatusSwitch
            className="mt-0"
            checked={state.isActive}
            onCheckedChange={state.setIsActive}
          />
        </FormSectionCard>
      )}
    </div>
  )
}
